// Seat usage — the athlete and coach counts a company's subscription plan is
// measured against (see `features/subscription-seats.md`).
//
// Plans themselves are not modelled yet. What this module owns is the one thing
// that must never be re-derived per screen: **what counts as a seat**. A club is
// billed for everyone it trains, so a seat is any live athlete on its roster —
// the ones registered under it and the ones it carries from another club alike.
// They are all athletes of the club as far as a plan is concerned, and are
// counted as one figure; only the coach mark splits the roster, because a club
// manages its coaching staff as its own list.
//
// Pure and framework-free: the server aggregates these counts in the store and
// the client renders them, but both read the total from `countBillableSeats`, so
// an admin can never be shown a number a future plan check would disagree with.

import type { Athlete } from './types';

/**
 * The raw per-company counts a store aggregates. Both figures cover **live**
 * rows only — soft-deleted athletes and ended memberships are not seats (see
 * `features/soft-delete.md`).
 */
export interface CompanySeatCounts {
  companyId: number;
  /** Athletes on the club's roster (coaches excluded). */
  athletes: number;
  /** Coaches on the club's roster. */
  coaches: number;
}

/** Seat counts plus the total a subscription plan is measured against. */
export interface CompanySeatUsage extends CompanySeatCounts {
  /**
   * The plan-relevant total: everyone on the club's roster. The single place
   * the metric is defined — see `countBillableSeats`.
   */
  billableSeats: number;
}

/**
 * The number a subscription plan is measured against: every live athlete and
 * coach on the club's roster.
 *
 * Membership is what counts, not who owns the profile — the plan reflects what
 * the club trains, not who filed the paperwork. An athlete training at two clubs
 * therefore occupies a seat in each; that is deliberate, since both clubs run
 * classes, attendance and coaching for them.
 */
export function countBillableSeats(counts: CompanySeatCounts): number {
  return counts.athletes + counts.coaches;
}

/** Attach the derived total to aggregated counts. */
export function toCompanySeatUsage(counts: CompanySeatCounts): CompanySeatUsage {
  return { ...counts, billableSeats: countBillableSeats(counts) };
}

/** The zero usage of a company with nobody on its roster. */
export function emptySeatUsage(companyId: number): CompanySeatUsage {
  return { companyId, athletes: 0, coaches: 0, billableSeats: 0 };
}

/**
 * Seat usage derived from a club's roster — every live athlete with a live
 * membership of `companyId`, i.e. exactly what `listClubMembers` returns.
 *
 * The roster is split on the coach mark alone: where an athlete's profile is
 * owned makes no difference to the count, so a club's own athletes and the ones
 * it carries from elsewhere land in the same figure.
 *
 * The live implementation aggregates in SQL instead of listing rows, so this is
 * the reference definition the tests and in-memory stores share rather than the
 * hot path.
 */
export function summarizeCompanySeats(
  companyId: number,
  roster: readonly Athlete[],
): CompanySeatUsage {
  let athletes = 0;
  let coaches = 0;
  for (const athlete of roster) {
    if (athlete.isCoach) coaches += 1;
    else athletes += 1;
  }
  return toCompanySeatUsage({ companyId, athletes, coaches });
}

/**
 * The usage of one company out of a list, or a zero usage when it is absent —
 * which is the honest reading for a company nobody has joined yet (a store
 * aggregate has no row to return for it).
 */
export function findSeatUsage(
  usages: readonly CompanySeatUsage[],
  companyId: number,
): CompanySeatUsage {
  return (
    usages.find((usage) => usage.companyId === companyId) ?? emptySeatUsage(companyId)
  );
}
